import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import * as Collapsible from '@radix-ui/react-collapsible';
import { updateCustomField } from 'api/campaigns';
import * as CampaignToolsCustomFieldForm from 'assets/js/pages/campaign-settings/campaign-tools/CampaignToolsCustomFieldForm';
import * as Form from 'components/form';
import { omit } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignCustomField, CustomField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface EditCustomFieldProps {
    field: CustomField;
    campaignId: number;
}

const CampaignToolsEditCustomField = ({ field, campaignId }: EditCustomFieldProps) => {
    const queryClient = useQueryClient();
    const { success, error } = useToast();

    const methods = Form.useGivebutterForm<CampaignCustomField>({
        defaultValues: {
            id: field.id,
            type: field.type,
            title: field.title,
            required: field.required,
            description: field.description || '',
            answers: field.answers
                ? field.answers.map(answer => ({ value: answer }))
                : [{ value: '' }],
        },
    });

    const { reset, handleSubmit, watch } = methods;

    const formType = watch('type');
    const isOptionType = formType && ['select', 'multiselect', 'radio'].includes(formType);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (data: CampaignCustomField) => {
            const sanitizedAnswers = isOptionType
                ? data?.answers?.map(answer => (typeof answer === 'object' ? answer.value : answer))
                : [];

            let submissionData = {
                ...(isOptionType ? data : omit(data, 'answers')),
                ...(isOptionType && { answers: sanitizedAnswers }),
            };

            return updateCustomField(campaignId, field.id, submissionData);
        },
        onSuccess: (_, updated) => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.customFields(campaignId),
            });
            success(`The ${formType} custom field has been updated!`);
            reset(updated);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <Form.Provider {...methods}>
            <Box className="w-full p-4 md:p-8">
                <form onSubmit={handleSubmit(submit as SubmitHandler<CampaignCustomField>)}>
                    <CampaignToolsCustomFieldForm.Container>
                        <CampaignToolsCustomFieldForm.Title />
                        <CampaignToolsCustomFieldForm.Description />
                        <CampaignToolsCustomFieldForm.IsRequired />

                        <Separator />

                        {isOptionType && <CampaignToolsCustomFieldForm.Answers />}

                        <CampaignToolsCustomFieldForm.Submit isLoading={isLoading}>
                            <Collapsible.Trigger asChild>
                                <Button variant="transparent">Cancel</Button>
                            </Collapsible.Trigger>
                        </CampaignToolsCustomFieldForm.Submit>
                    </CampaignToolsCustomFieldForm.Container>
                </form>
            </Box>
        </Form.Provider>
    );
};

export { CampaignToolsEditCustomField };
